import { Fragment } from 'react';

import { splitOnHighlight } from '../helpers';
import * as styled from '../styles/profile-card';
import { FeaturedReview } from './featured-review';
import { PersonAvatarIcon } from './person-avatar-icon';
import { ReputationPanel } from './reputation-panel';
import { SocialIconRow } from './social-icon-row';
import { TrustBadges } from './trust-badges';

type Props = {
  data: LandingV2.ProfileCardData;
  accentColor?: string | null;
  avatarBorderColor?: string | null;
  themePreset?: LandingV2.ThemePreset;
};

export const ProfileCard = ({ data, accentColor, avatarBorderColor, themePreset = 'personal' }: Props) => {
  const headline = splitOnHighlight(data.headerHeadline, data.headerHeadlineHighlight);

  return (
    <styled.Card>
      <styled.BannerWrap>
        <styled.Banner>
          <styled.BannerFade />
          <styled.BannerTopLeft>
            <styled.BannerBrandRow>
              <styled.BannerLogoMark $accentColor={accentColor}>K</styled.BannerLogoMark>
              <styled.BannerWordmark>kudable.com</styled.BannerWordmark>
            </styled.BannerBrandRow>
            <styled.BannerHeadline $accentColor={accentColor}>
              {headline.before}
              <em>{headline.highlight}</em>
              {headline.after}
            </styled.BannerHeadline>
          </styled.BannerTopLeft>
          <styled.BannerHandle>{data.handle}</styled.BannerHandle>
        </styled.Banner>
        <styled.AvatarCircle
          $background={data.avatarBg}
          $borderColor={avatarBorderColor}
          $square={themePreset === 'business'}
        >
          <PersonAvatarIcon variant={data.avatarVariant} size={92} color={data.avatarFg} />
        </styled.AvatarCircle>
      </styled.BannerWrap>

      <styled.Body>
        <styled.Name>{data.name}</styled.Name>
        <styled.OnlineRow>
          <styled.OnlineDot />
          <styled.OnlineLabel>online now</styled.OnlineLabel>
        </styled.OnlineRow>
        <styled.Subtitle>{data.subtitle}</styled.Subtitle>
        <styled.Tagline>{data.tagline}</styled.Tagline>

        {data.metrics && (
          <styled.MetricsRow>
            {data.metrics.map((metric, index) => (
              <Fragment key={metric.label}>
                {index > 0 && <styled.MetricDivider />}
                <styled.Metric>
                  <styled.MetricValue>{metric.value}</styled.MetricValue>
                  <styled.MetricLabel>{metric.label}</styled.MetricLabel>
                </styled.Metric>
              </Fragment>
            ))}
          </styled.MetricsRow>
        )}

        <TrustBadges badges={data.badges} accentColor={accentColor} />
        <SocialIconRow platforms={data.socials} />
        <styled.PrimaryCta type="button" $accentColor={accentColor}>
          {data.ctaLabel}
        </styled.PrimaryCta>
        <ReputationPanel stat={data.stat} accentColor={accentColor} />
        <FeaturedReview review={data.featuredReview} />
      </styled.Body>
    </styled.Card>
  );
};
